import { PreferenceDto, SavedSearch } from '@creditsights/api-ui-web';
import React, { useEffect, useRef, useState } from 'react';
import useUpdateRecentSearches from '../../../../hooks/preferences/useUpdateRecentSearches';
import { RecentSearch } from '../../../../models/RecentSearch';
import ListTitleContainer from './ListTitleContainer/ListTitleContainer';
import RecentSearchesList from './RecentSearchesList/RecentSearchesList';
import SavedSearchesList from './SavedSearchesList/SavedSearchesList';

interface RecentAndSavedSuggestsProps {
  verticalIndex: number | null;
  navigateToSavedSearch: (savedSearch: SavedSearch) => void;
  onSuggestionClick: (selectedTerm: string) => void;
  onLastRecentSearchRemoved: () => void;
  savedSearches?: SavedSearch[];
  allRecentSearches?: PreferenceDto<RecentSearch[]>;
  horizontalIndex: 0 | 1;
  recentSearches?: PreferenceDto<RecentSearch[]>;
  setRecentSearches: (searches: PreferenceDto<RecentSearch[]>) => void;
}

const RecentAndSavedSuggests = ({
  verticalIndex,
  navigateToSavedSearch,
  savedSearches,
  onSuggestionClick,
  allRecentSearches,
  onLastRecentSearchRemoved,
  horizontalIndex,
  recentSearches,
  setRecentSearches,
}: RecentAndSavedSuggestsProps): React.ReactElement => {
  const [searchTermsToRemove, setSearchTermsToRemove] = useState<string[]>([]);
  const { mutate } = useUpdateRecentSearches();
  const componentWillUnmount = useRef(false);
  const shouldRemoveOnUnmount = useRef(true);

  useEffect(() => {
    return () => {
      componentWillUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      const shouldUpdateRecentSearchesOnUnmount =
        componentWillUnmount.current &&
        shouldRemoveOnUnmount.current &&
        searchTermsToRemove.length &&
        recentSearches &&
        allRecentSearches;
      if (shouldUpdateRecentSearchesOnUnmount) {
        sendRequestToRemoveRecentSearches(allRecentSearches, recentSearches);
      }
    };
  }, [searchTermsToRemove, recentSearches, allRecentSearches, savedSearches]);

  const removeRecentSearch = (searchTerm: string) => {
    if (recentSearches) {
      const isLastRecentSearchRemoved = recentSearches.value.length === 1;
      const shouldSendAPIRequestOnRemove = !savedSearches?.length && isLastRecentSearchRemoved && allRecentSearches;
      if (shouldSendAPIRequestOnRemove) {
        shouldRemoveOnUnmount.current = false;
        sendRequestToRemoveRecentSearches(allRecentSearches, allRecentSearches, searchTerm);
      }
      if (isLastRecentSearchRemoved) {
        onLastRecentSearchRemoved();
      }
      updateLocalRecentSearchesState(recentSearches, searchTerm);
    }
  };

  const sendRequestToRemoveRecentSearches = (
    allRecentSearches: PreferenceDto<RecentSearch[]>,
    recentSearches: PreferenceDto<RecentSearch[]>,
    searchTerm?: string
  ) => {
    const recentSearchesToRemove = [...searchTermsToRemove, searchTerm];
    const updatedRecentSearches = allRecentSearches.value.filter(
      (recentSearch) => !recentSearchesToRemove.includes(recentSearch.searchTerm)
    );
    mutate({ id: recentSearches.id, value: updatedRecentSearches });
  };

  const updateLocalRecentSearchesState = (recentSearches: PreferenceDto<RecentSearch[]>, searchTerm: string) => {
    const updatedRecentSearches = recentSearches.value.filter((recentSearch) => recentSearch.searchTerm !== searchTerm);
    setSearchTermsToRemove((prevState) => [...prevState, searchTerm]);
    setRecentSearches({ ...recentSearches, value: updatedRecentSearches });
  };

  return (
    <>
      {savedSearches && savedSearches.length > 0 && (
        <ListTitleContainer title="Saved Searches">
          <SavedSearchesList
            savedSearches={savedSearches}
            verticalIndex={verticalIndex}
            navigateToSavedSearch={navigateToSavedSearch}
          />
        </ListTitleContainer>
      )}
      {recentSearches && recentSearches.value.length > 0 && (
        <ListTitleContainer title="Recent Searches">
          <RecentSearchesList
            recentSearches={recentSearches}
            savedSearchesCount={savedSearches?.length}
            verticalIndex={verticalIndex}
            onSuggestionClick={onSuggestionClick}
            removeRecentSearch={removeRecentSearch}
            horizontalIndex={horizontalIndex}
          />
        </ListTitleContainer>
      )}
    </>
  );
};

export default RecentAndSavedSuggests;
